import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { recurring as api, recurringGenerateNow, apiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { PageHeader, Card, Loading } from "@/components/Shared";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Zap, RefreshCw } from "lucide-react";

export default function RecurringDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [r, setR] = useState(null);

  const load = () => api.get(id).then(setR).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [id]);
  if (!r) return <Loading />;

  const perInvoice = r.line_items?.reduce((a, li) => a + li.quantity * li.unit_price, 0) || 0;
  const genNow = async () => { try { const inv = await recurringGenerateNow(id); toast.success(`Generated ${inv.number}`); nav(`/invoices/${inv.id}`); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div>
      <button onClick={() => nav("/recurring")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0B132B] mb-4" data-testid="recurring-detail-back"><ArrowLeft className="w-4 h-4" /> Back</button>
      <PageHeader title={r.title} subtitle={`${r.customer?.name} · ${r.frequency}`}>
        {r.status === "active" && <Button variant="outline" onClick={genNow} data-testid="detail-generate-now"><Zap className="w-4 h-4 mr-1.5" /> Generate Now</Button>}
        <Button onClick={() => nav(`/recurring/${id}/edit`)} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="detail-edit-recurring"><Pencil className="w-4 h-4 mr-1.5" /> Edit</Button>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4"><h3 className="font-display font-semibold text-[#0B132B]">Schedule</h3><StatusBadge status={r.status} /></div>
          <div className="space-y-2.5 text-sm">
            <Row label="Per invoice" value={money(perInvoice)} />
            <Row label="Frequency" value={<span className="capitalize">{r.frequency}</span>} />
            <Row label="Start" value={fmtDate(r.start_date)} />
            <Row label="End" value={r.end_date ? fmtDate(r.end_date) : "No end"} />
            <Row label="Payment terms" value={`${r.payment_terms} days`} />
            <Row label="Next invoice" value={r.status === "active" ? fmtDate(r.next_invoice_date) : "—"} />
            <Row label="Generated" value={r.generated_count || 0} />
            <Row label="Last generated" value={r.last_generated_at ? fmtDate(r.last_generated_at) : "—"} />
          </div>
        </Card>

        <Card className="lg:col-span-2 overflow-hidden">
          <div className="p-5 border-b border-slate-100 flex items-center gap-2"><RefreshCw className="w-4 h-4 text-[#D4AF37]" /><h3 className="font-display font-semibold text-[#0B132B]">Generated Invoices</h3></div>
          {r.history?.length ? (
            <div className="divide-y divide-slate-100">
              {r.history.map((i) => (
                <button key={i.id} onClick={() => nav(`/invoices/${i.id}`)} className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 text-left" data-testid={`recurring-history-${i.id}`}>
                  <div><div className="font-semibold text-[#0B132B] text-sm">{i.number}</div><div className="text-xs text-slate-400">{fmtDate(i.issue_date)}</div></div>
                  <div className="flex items-center gap-3"><span className="text-sm font-semibold text-[#0B132B]">{money(i.total)}</span><StatusBadge status={i.display_status} /></div>
                </button>
              ))}
            </div>
          ) : <div className="p-10 text-center text-sm text-slate-400">No invoices generated yet.</div>}
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between"><span className="text-slate-500">{label}</span><span className="font-medium text-[#0B132B]">{value}</span></div>;
}
